import { TextSelection } from "@tiptap/pm/state";

import { deleteNode } from "@/input/utils/editOperators";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";

const SPLIT_SYMBOLS = [" "];

export function shouldSplitInlineExpression(value: string) {
  if (!value || value.length === 0) {
    return false;
  }
  return SPLIT_SYMBOLS.includes(value.charAt(value.length - 1));
}

export function splitInlineExpression(doc: DocumentModel, insertValue?: string) {
  const [value, nodeToCleanup] = convertValue(doc, (insertValue ?? doc.input.value) || " ");

  doc.editor.resetInput();

  if (doc.editor.tiptap && doc.cursor.node) {
    if (nodeToCleanup) {
      deleteNode(nodeToCleanup, doc.cursor.index);
    }

    const { node, index } = doc.cursor;
    const tipTapCursor = doc.editor.tiptap?.state.selection.anchor || 0;

    // Insert `value + space` before inline expression
    if (index === 0 && node.parent?.is(WrapperNodeModel)) {
      doc.transact(() => {
        doc.editor.tiptap
          ?.chain()
          .setMeta("implicit", true)
          .insertContentAt(tipTapCursor, value)
          .setTextSelection(tipTapCursor + value.length)
          .run();

        doc.cursor.reset();
        doc.cursor.moveTo(node, 0);
      }, "splitInlineExpression:before");

      return tipTapCursor;
    }
    // Split inline expression into two, placing tiptap cursor between them
    if ((index || 0) < node.nodes.length && node.parent?.is(WrapperNodeModel)) {
      const { parent } = node;
      doc.transact(() => {
        doc.computedNodes.setComputed(parent, false);
        const newInlineNode = doc.create(InlineNodeModel);
        const textLength = value !== " " ? value.length : 0;

        newInlineNode.appendChildren(node.nodes.slice(index));
        doc.cursor.reset();

        doc.editor.tiptap
          ?.chain()
          .setMeta("implicit", true)
          .setTextSelection(tipTapCursor + 1)
          .command(({ chain }) => {
            if (value !== " ") {
              return chain().insertContent(value).run();
            }
            return true;
          })
          .insertInlineExpression(newInlineNode)
          .command(({ tr, dispatch }) => {
            if (dispatch) {
              tr.setSelection(TextSelection.create(tr.doc, tipTapCursor + 1 + textLength));
            }
            return true;
          })
          .run();
      }, "splitInlineExpression:middle");

      doc.editor.tiptap?.view.focus();

      return value !== " " ? tipTapCursor + 1 : null;
    }
    // Insert `space + value` after inline expression
    if (index === node.nodes.length || !node.parent?.is(WrapperNodeModel)) {
      doc.transact(() => {
        doc.cursor.reset();
        doc.editor.tiptap
          ?.chain()
          .setMeta("implicit", true)
          .command(({ chain }) => {
            let chained = chain().setTextSelection({
              from: tipTapCursor + 1,
              to: tipTapCursor + 1,
            });

            if (value !== " " || shouldInsertSpace(node)) {
              chained = chained.insertContent(value);
            }

            return chained.run();
          })
          .run();
      }, "splitInlineExpression:after");

      doc.editor.tiptap?.view.focus();

      return value !== " " ? tipTapCursor + 1 : null;
    }
  }

  return null;
}

function shouldInsertSpace(inlineNode: InlineNodeModel) {
  if (!inlineNode.parent?.is(WrapperNodeModel)) {
    return false;
  }

  if (inlineNode.nodes.length === 0 || inlineNode.nodes.length > 1) {
    return false;
  }

  const [node] = inlineNode.nodes;

  switch (node.type) {
    case "number": {
      return !node.unitNode || node.unitNode.placement === "prefix";
    }

    case "operatorSymbol": {
      return true;
    }

    default: {
      return false;
    }
  }
}

function convertValue(doc: DocumentModel, value: string): [string] | [string, NodeModel] {
  const { node } = doc.cursor;
  const { parent } = node ?? {};

  if (!parent) {
    return [value];
  }

  switch (parent.type) {
    case "matrix": {
      const { parent: group } = parent;
      const { level } = doc.cursor;

      if (level === 1 && group?.is(GroupNodeModel) && group.inlineNode?.isEmpty) {
        return [`${group.openingSymbol ?? ""}${value}${group.closingSymbol ?? ""}`, group];
      }

      return [value];
    }

    default: {
      return [value];
    }
  }
}
